import React from 'react';

/**
 * Fires a best-effort beacon to the backend when something crashes in
 * the browser. Three sources:
 *   • React render crash (caught by componentDidCatch below)
 *   • window.onerror (sync JS errors outside React)
 *   • window.onunhandledrejection (async promise rejections)
 *
 * Dedupes identical messages within 10s so a tight loop can't DoS
 * the beacon endpoint.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const _recent = new Map(); // msg → ts

function reportFrontendError(payload) {
  try {
    const key = `${payload.route || ''}::${payload.error_message || ''}`;
    const now = Date.now();
    const prev = _recent.get(key);
    if (prev && now - prev < 10_000) return;
    _recent.set(key, now);
    if (_recent.size > 50) {
      // crude GC
      const cutoff = now - 60_000;
      for (const [k, v] of _recent.entries()) if (v < cutoff) _recent.delete(k);
    }

    const session = (() => {
      try {
        const raw = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
        if (!raw) return null;
        return JSON.parse(localStorage.getItem(raw) || 'null');
      } catch { return null; }
    })();

    const body = JSON.stringify({
      route: payload.route || window.location.pathname + window.location.search,
      error_message:   (payload.error_message || '').toString().slice(0, 2000),
      error_stack:     (payload.error_stack   || '').toString().slice(0, 8000),
      component_stack: (payload.component_stack || '').toString().slice(0, 8000),
      user_id:   session?.user?.id,
      viewport_w: window.innerWidth,
      viewport_h: window.innerHeight,
      backend_request_id: payload.backend_request_id || undefined,
      context:   payload.context || {},
    });
    // sendBeacon survives page unload. Falls back to fetch for larger payloads.
    const url = `${BACKEND}/api/health/frontend-error`;
    if (navigator.sendBeacon && body.length < 60_000) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body, keepalive: true,
      }).catch(() => {});
    }
  } catch { /* never let the reporter be the thing that fails */ }
}

// Install global listeners once per page load.
let _installed = false;
export function installFrontendErrorReporting() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  window.addEventListener('error', (e) => {
    reportFrontendError({
      error_message: e.message,
      error_stack:   e.error?.stack || '',
      context:       { source: e.filename, lineno: e.lineno, colno: e.colno },
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    reportFrontendError({
      error_message: (r?.message || String(r || '')).slice(0, 2000),
      error_stack:   r?.stack || '',
      context:       { source: 'unhandledrejection' },
    });
  });
}

// Expose for the fetch wrapper (see api.js if we add one later).
export { reportFrontendError };


export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, message: (err?.message || 'Something went wrong').toString().slice(0, 400) };
  }
  componentDidCatch(err, info) {
    reportFrontendError({
      error_message:   err?.message || String(err),
      error_stack:     err?.stack || '',
      component_stack: info?.componentStack || '',
      context:         { source: 'error_boundary' },
    });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: 40, maxWidth: 520, margin: '80px auto', textAlign: 'center',
          background: '#fff', border: '1px solid #fee2e2', borderRadius: 12,
        }} data-testid="error-boundary-fallback">
          <h2 style={{ margin: 0, color: '#991b1b' }}>Something went wrong</h2>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 8 }}>
            We've logged the error and the team has been notified. Try reloading the page.
          </p>
          <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 12 }}>
            <code>{this.state.message}</code>
          </p>
          <button onClick={() => window.location.reload()} style={{
            marginTop: 18, padding: '8px 20px', background: '#4f46ff', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
          }}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}
